// ExecFinance.js — "การเงินและธนาคารนักเรียน" (pos 90/99)
//
// คำถามของหน้านี้: เก็บค่าธรรมเนียมได้ตามเป้าไหม ค้างกระจุกที่ไหน และเงินออม
// นักเรียนโตขึ้นไหม
//
// ส่วนยอดรวมย้ายมาจากหน้าภาพรวมวันนี้ ที่เพิ่มคือการเจาะว่าค้างอยู่ห้องไหน ซึ่ง
// เป็นสิ่งที่ผู้บริหารต้องรู้เพื่อกำชับครูประจำชั้นให้ถูกคน
import React, { useMemo } from "react";
import { Badge, Col, ProgressBar, Row, Table } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import {
  ExecPage, Panel, SectionTitle, StatCard, EmptyState, ScrollTable,
  YearPicker, useExecYear, useExecData,
  fmtNum, fmtPct, fmtBaht, toNum,
} from "./ExecShell";

const shortClass = (name) => String(name || "").replace("ชั้นมัธยมศึกษาปีที่ ", "ม.");

export default function ExecFinance() {
  const { years, yearId, setYearId } = useExecYear();
  const query = useMemo(() => ({ year_id: yearId }), [yearId]);
  const { data, loading, error } = useExecData("/api/executive/stats/finance", query);

  const overall = useMemo(() => (data?.overall || { available: false, overall: {}, overdue: {}, byTerm: [] }), [data]);
  const fee = overall.overall || {};
  const overdue = overall.overdue || {};
  const byTerm = useMemo(() => (Array.isArray(overall.byTerm) ? overall.byTerm : []), [overall]);
  const byClass = useMemo(() => (data?.byClass || { available: false, rows: [] }), [data]);
  const topRooms = Array.isArray(data?.topRooms) ? data.topRooms : [];
  const curve = useMemo(() => (data?.curve || { available: false, buckets: [] }), [data]);
  const debtors = data?.debtors || { available: false, rows: [] };
  const bank = useMemo(() => (data?.bank || { available: false, byMonth: [] }), [data]);
  const fines = data?.fines || { available: false };

  const hasFeeData = overall.available && toNum(fee.invoice_count) > 0;

  const classChartOptions = useMemo(() => ({
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    colors: ["#198754", "#dc3545"],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "52%" } },
    dataLabels: { enabled: false },
    legend: { position: "top", fontSize: "12px" },
    xaxis: { categories: (byClass.rows || []).map((r) => shortClass(r.c_class)), labels: { style: { fontSize: "11px" } } },
    yaxis: { labels: { formatter: (v) => `${Math.round(v / 1000)}k`, style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { shared: true, intersect: false, y: { formatter: (v) => fmtBaht(v) } },
  }), [byClass]);

  const curveOptions = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#0d6efd"],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "50%" } },
    dataLabels: { enabled: true, formatter: (v) => (v ? `${v}` : ""), style: { fontSize: "10px" } },
    legend: { show: false },
    xaxis: { categories: curve.buckets.map((b) => b.label), labels: { style: { fontSize: "10px" } } },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
  }), [curve]);

  const bankOptions = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#198754", "#fd7e14"],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "56%" } },
    dataLabels: { enabled: false },
    legend: { position: "top", fontSize: "12px" },
    xaxis: { categories: bank.byMonth.map((m) => m.month), labels: { style: { fontSize: "10px" } } },
    yaxis: { labels: { formatter: (v) => fmtNum(v), style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { shared: true, intersect: false, y: { formatter: (v) => fmtBaht(v) } },
  }), [bank]);

  return (
    <ExecPage
      title="การเงินและธนาคารนักเรียน"
      icon="ri-money-dollar-circle-line"
      lead="อัตราการเก็บค่าธรรมเนียม ห้องที่ค้างมากที่สุด และเงินออมของนักเรียน"
      activeYear={data?.year}
      loading={loading}
      error={error}
      toolbar={<YearPicker years={years} yearId={yearId} onChange={setYearId} activeYear={data?.year} />}
    >
      {!hasFeeData && (
        <div className="alert alert-light border fs-sm mb-4">
          <i className="ri-information-line me-2" />
          ยังไม่มีใบแจ้งหนี้ในปีการศึกษานี้ ส่วนค่าธรรมเนียมจะแสดงเมื่องานการเงินออกใบแจ้งหนี้แล้ว
        </div>
      )}

      {hasFeeData && (
        <>
          <Row className="g-3 mb-4">
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-bill-fill" color="primary" label="ยอดเรียกเก็บทั้งหมด"
                value={fmtBaht(fee.total_amount)}
                sub={`${fmtNum(fee.invoice_count)} ใบแจ้งหนี้`}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-hand-coin-fill"
                color={toNum(fee.collectionRate) >= 80 ? "success" : "warning"}
                label="เก็บได้แล้ว"
                value={fmtBaht(fee.paid_amount)}
                sub={`อัตราการเก็บ ${fmtPct(fee.collectionRate)}`}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-wallet-3-fill"
                color={toNum(fee.outstanding) > 0 ? "warning" : "success"}
                label="ค้างชำระ"
                value={fmtBaht(fee.outstanding)}
                sub={`${fmtNum(toNum(fee.unpaid_count) + toNum(fee.partial_count))} รายที่ยังค้าง`}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-alarm-warning-fill"
                color={toNum(overdue.count) > 0 ? "danger" : "secondary"}
                label="เกินกำหนดชำระ"
                value={fmtNum(overdue.count)}
                sub={toNum(overdue.count) > 0 ? `ยอด ${fmtBaht(overdue.amount)}` : "ไม่มีรายการเกินกำหนด"}
                tone={toNum(overdue.count) > 0 ? "danger" : undefined}
              />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xl={7}>
              <Panel title="เก็บได้เทียบค้าง รายระดับชั้น">
                {!byClass.available || byClass.rows.length === 0 ? <EmptyState compact /> : (
                  <ReactApexChart
                    options={classChartOptions}
                    series={[
                      { name: "เก็บได้", data: byClass.rows.map((r) => toNum(r.paid_amount)) },
                      { name: "ค้าง", data: byClass.rows.map((r) => toNum(r.outstanding)) },
                    ]}
                    type="bar" height={300}
                  />
                )}
              </Panel>
            </Col>
            <Col xl={5}>
              <Panel title="สถานะรายภาคเรียน">
                {byTerm.length === 0 ? <EmptyState compact /> : byTerm.map((term) => {
                  const rate = toNum(term.collectionRate);
                  return (
                    <div key={term.term_id} className="border rounded p-3 mb-2">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <strong>{term.term_name || `ภาคเรียนที่ ${term.semester}`}</strong>
                        <Badge bg={rate >= 80 ? "success" : rate >= 50 ? "warning" : "danger"}>{fmtPct(rate)}</Badge>
                      </div>
                      <ProgressBar style={{ height: 8 }} now={rate} variant={rate >= 80 ? "success" : rate >= 50 ? "warning" : "danger"} />
                      <div className="d-flex gap-3 flex-wrap fs-xs text-secondary mt-2">
                        <span>เรียกเก็บ {fmtBaht(term.total_amount)}</span>
                        <span className="text-success">เก็บได้ {fmtBaht(term.paid_amount)}</span>
                        <span className="text-danger">ค้าง {fmtBaht(term.outstanding)}</span>
                      </div>
                    </div>
                  );
                })}
              </Panel>
            </Col>
          </Row>

          <SectionTitle icon="ri-error-warning-line" title="ค้างชำระอยู่ที่ไหน" note="เรียงตามยอดค้าง เพื่อให้รู้ว่าต้องกำชับครูประจำชั้นห้องไหนก่อน" color="danger" />
          <Row className="g-3 mb-4">
            <Col xl={7}>
              <Panel title="ห้องที่ค้างมากที่สุด">
                {topRooms.length === 0 ? <EmptyState message="ไม่มีห้องที่ค้างชำระ" compact /> : (
                  <ScrollTable>
                    <Table className="table-sm mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>ห้อง</th>
                          <th>ครูประจำชั้น</th>
                          <th className="text-end">นักเรียนที่ค้าง</th>
                          <th className="text-end">ยอดค้าง</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topRooms.map((room) => (
                          <tr key={room.r_id}>
                            <td className="fw-semibold">{room.room_label}</td>
                            <td className="fs-xs text-secondary text-truncate" style={{ maxWidth: 180 }}>
                              {room.advisor_names || "ยังไม่กำหนด"}
                            </td>
                            <td className="text-end">{fmtNum(room.students_owing)}</td>
                            <td className="text-end fw-semibold text-danger">{fmtBaht(room.outstanding)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </ScrollTable>
                )}
              </Panel>
            </Col>
            <Col xl={5}>
              <Panel
                title="ค้างข้ามภาคเรียน"
                note="กลุ่มที่การทวงตามปกติไม่ได้ผลแล้ว มักต้องคุยเรื่องผ่อนผันหรือทุน"
              >
                {!debtors.available || debtors.total === 0 ? (
                  <EmptyState message="ไม่มีนักเรียนที่ค้างข้ามภาค" compact />
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-end mb-3 border-bottom pb-3">
                      <div>
                        <div className="fs-4 fw-semibold">{fmtNum(debtors.total)} คน</div>
                        <div className="fs-xs text-secondary">ค้างมากกว่าหนึ่งภาคเรียน</div>
                      </div>
                      <div className="text-end">
                        <div className="fs-5 fw-semibold text-danger">{fmtBaht(debtors.amount)}</div>
                        <div className="fs-xs text-secondary">ยอดค้างรวม</div>
                      </div>
                    </div>
                    {debtors.rows.slice(0, 10).map((row) => (
                      <div key={row.st_id} className="d-flex justify-content-between border-bottom py-2 fs-sm">
                        <span className="text-truncate">
                          {row.st_name}
                          <span className="text-secondary fs-xs ms-2">{row.room_label || "—"}</span>
                        </span>
                        <span className="flex-shrink-0">
                          <Badge bg="light" text="dark" className="fw-normal me-2">{fmtNum(row.terms_owing)} ภาค</Badge>
                          <span className="fw-semibold">{fmtBaht(row.outstanding)}</span>
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </Panel>
            </Col>
          </Row>

          {curve.available && curve.total > 0 && (
            <Row className="g-3 mb-4">
              <Col xl={7}>
                <Panel
                  title="ผู้ปกครองชำระเงินเมื่อไหร่"
                  note={`นับจากวันออกใบแจ้งหนี้ เฉลี่ย ${fmtNum(curve.avgDays)} วัน`}
                >
                  <ReactApexChart
                    options={curveOptions}
                    series={[{ name: "จำนวนครั้ง", data: curve.buckets.map((b) => toNum(b.total)) }]}
                    type="bar" height={260}
                  />
                </Panel>
              </Col>
              <Col xl={5}>
                <Panel title="สัดส่วนการชำระ">
                  {curve.buckets.map((b) => (
                    <div key={b.key} className="mb-3">
                      <div className="d-flex justify-content-between fs-sm mb-1">
                        <span>{b.label}</span>
                        <span className="fw-semibold">{fmtPct(b.percent)}</span>
                      </div>
                      <ProgressBar style={{ height: 7 }} now={toNum(b.percent)} variant="primary" />
                    </div>
                  ))}
                </Panel>
              </Col>
            </Row>
          )}
        </>
      )}

      <SectionTitle icon="ri-bank-line" title="ธนาคารนักเรียน" note="บัญชีออมทรัพย์ของนักเรียนที่โรงเรียนดูแล" color="success" />
      <Row className="g-3 mb-4">
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-bank-card-fill" color="success" label="บัญชีที่เปิดใช้งาน"
            value={bank.available ? fmtNum(bank.activeAccounts) : "—"}
            sub={bank.available ? `จากทั้งหมด ${fmtNum(bank.accounts)} บัญชี` : "ยังไม่มีข้อมูลธนาคาร"}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-safe-2-fill" color="primary" label="ยอดเงินฝากรวม"
            value={bank.available ? fmtBaht(bank.balance) : "—"}
            sub="ยอดคงเหลือของบัญชีที่ยังใช้งาน"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-arrow-up-circle-fill" color="info" label="ฝากรวมปีนี้"
            value={bank.available ? fmtBaht(bank.byMonth.reduce((s, m) => s + toNum(m.deposit), 0)) : "—"}
            sub={`${fmtNum(bank.byMonth?.reduce((s, m) => s + toNum(m.txns), 0))} ธุรกรรม`}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-book-2-fill"
            color={toNum(fines.unpaid) > 0 ? "warning" : "secondary"}
            label="ค่าปรับห้องสมุดค้าง"
            value={fines.available ? fmtBaht(fines.amount) : "—"}
            sub={fines.available ? `${fmtNum(fines.unpaid)} รายการ` : "ยังไม่มีข้อมูลห้องสมุด"}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Panel title="การฝากถอนรายเดือน" note="เงินออมของนักเรียนเพิ่มขึ้นหรือลดลง">
            {!bank.available || bank.byMonth.length === 0 ? (
              <EmptyState message="ยังไม่มีธุรกรรมในช่วงนี้" />
            ) : (
              <ReactApexChart
                options={bankOptions}
                series={[
                  { name: "ฝาก", data: bank.byMonth.map((m) => toNum(m.deposit)) },
                  { name: "ถอน", data: bank.byMonth.map((m) => toNum(m.withdraw)) },
                ]}
                type="bar" height={280}
              />
            )}
          </Panel>
        </Col>
      </Row>
    </ExecPage>
  );
}
